import {
  ReactNode,
  useRef,
  useState,
  useCallback,
  useEffect,
  createContext,
  useContext,
} from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { autoScrollDelta, scrollableAncestor } from "./autoScroll";

export interface DragAndDropContextValue {
  dragItemIndex: number | null;
  dragOverIndex: number | null;
  isDragging: boolean;
  onDragStart: (index: number) => void;
  onDragEnter: (index: number) => void;
  onDragEnd: () => void;
}

const DragAndDropContext = createContext<DragAndDropContextValue | null>(null);

export const useDragAndDropContext = () => {
  const context = useContext(DragAndDropContext);
  if (!context) {
    throw new Error("useDragAndDropContext must be used within DragAndDrop");
  }
  return context;
};

/**
 * 순서 변경 가능한 목록 컨테이너 props — HTML5 Drag and Drop API 사용.
 *
 * `FileList.onItemsDrop` 와 다름:
 * - `DragAndDrop`: 기존 목록 순서 재정렬용.
 * - `FileList.onItemsDrop`: 외부 파일/폴더 드롭으로 새 항목 추가용.
 */
export interface DragAndDropProps {
  children: ReactNode;
  /** drop 완료 시 호출. `(fromIndex, toIndex)` 로 재배치 처리. */
  onDrop: (fromIndex: number, toIndex: number) => void;
  sx?: SxProps<Theme>;
}

/**
 * 드래그 가능한 개별 아이템 props — `DragAndDrop` 자식으로 사용.
 *
 * `renderDropZone` 으로 드롭 위치(위/아래) 미리보기를 커스터마이즈할 수 있다.
 */
export interface DragAndDropItemProps {
  children: ReactNode;
  index: number;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  renderDropZone?: (
    isDragOver: boolean,
    isDragged: boolean,
    position: "above" | "below" | null
  ) => ReactNode;
}

/**
 * 끌리는 항목의 흐림. 같은 요소에 두 경로로 적힌다 — dragstart 의 인라인 `style.opacity` 와
 * sx 의 `isDragged` 분기. 인라인이 sx 클래스보다 우선하므로 한쪽만 바꾸면 그 변경이 묻히거나
 * 화면과 어긋난 선언이 남아, 한 값으로 묶는다. `DragAndDrop.stories.tsx` 의 `meta` docs 설명에
 * 적힌 "(0.5)" 는 이 상수에 묶이지 않은 별도 기재다.
 */
const DRAGGED_ITEM_OPACITY = 0.5;

const DragAndDropItem = ({
  children,
  index,
  disabled = false,
  sx,
  renderDropZone,
}: DragAndDropItemProps) => {
  const context = useDragAndDropContext();
  const {
    dragItemIndex,
    dragOverIndex,
    isDragging,
    onDragStart,
    onDragEnter,
    onDragEnd,
  } = context;

  const isDragOver = dragOverIndex === index;
  const isDragged = dragItemIndex === index;

  const handleDragStart = useCallback(
    (e: React.DragEvent) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      onDragStart(index);
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/html", "");
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.opacity = String(DRAGGED_ITEM_OPACITY);
      }
    },
    [disabled, index, onDragStart]
  );

  const handleDragEnterLocal = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;
      e.preventDefault();
      e.stopPropagation();
      // isDragged 체크를 제거하여 원래 위치로 돌아왔을 때도 onDragEnter가 호출되도록 함
      // handleDragEnter에서 원래 위치인지 확인하여 dragOverIndex를 null로 설정함
      onDragEnter(index);
    },
    [disabled, index, onDragEnter]
  );

  const handleDragEndLocal = useCallback(
    (e: React.DragEvent) => {
      if (e.currentTarget instanceof HTMLElement) {
        e.currentTarget.style.opacity = "1";
      }
      onDragEnd();
    },
    [onDragEnd]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // 드롭존 위치 결정
  let dropZonePosition: "above" | "below" | null = null;
  if (
    isDragOver &&
    isDragging &&
    !isDragged &&
    dragItemIndex !== null &&
    dragItemIndex !== index
  ) {
    if (dragItemIndex < index) {
      dropZonePosition = "above";
    } else if (dragItemIndex > index) {
      dropZonePosition = "below";
    }
  }

  const defaultDropZoneSx: SxProps<Theme> = {
    "&::before": {
      ...dropIndicator,
      bottom: "100%",
      opacity: dropZonePosition === "below" ? 1 : 0,
    },
    "&::after": {
      ...dropIndicator,
      top: "100%",
      opacity: dropZonePosition === "above" ? 1 : 0,
    },
  };

  return (
    <Box
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragEnter={handleDragEnterLocal}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEndLocal}
      sx={{
        position: "relative",
        opacity: isDragged ? DRAGGED_ITEM_OPACITY : 1,
        transition: "opacity 0.2s ease-in-out",
        ...defaultDropZoneSx,
        ...sx,
      }}
    >
      {renderDropZone
        ? renderDropZone(isDragOver, isDragged, dropZonePosition)
        : children}
    </Box>
  );
};

export const DragAndDrop = ({ children, onDrop, sx }: DragAndDropProps) => {
  const dragItemIndexRef = useRef<number | null>(null);
  const dragOverItemIndexRef = useRef<number | null>(null);
  const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const pointerYRef = useRef<number | null>(null);
  const frameRef = useRef<number | null>(null);

  /**
   * 끌고 있는 동안 가장자리에서 목록을 민다 — HTML5 DnD 는 스크롤 컨테이너를 자동으로 밀어 주지
   * 않아, 목록이 화면보다 길면 끝의 항목을 반대쪽 끝으로 가져갈 길이 없다(끌고 있는 동안에는 휠도
   * 스크롤바도 잡을 수 없다).
   *
   * 포인터는 **캡처 단계의 document 리스너**로 읽는다. 항목의 `dragover` 가 `stopPropagation` 하므로
   * 컨테이너의 `onDragOver` 는 항목 위에서 불리지 않는데, 정작 스크롤이 필요한 자리가 바로 항목 위다.
   * 캡처는 target 에 닿기 전에 돌아 그 전파 차단의 영향을 받지 않는다.
   */
  const stopAutoScroll = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    pointerYRef.current = null;
  }, []);

  // scrollStep 은 스스로를 재귀 호출한다 — useCallback 안에서 같은 이름의 const 를
  // 직접 참조하면 "선언되기 전에 접근"으로 컴파일러가 막는다. useState 지연 초기화로
  // 마운트 시 1회만 클로저를 만들면 이후에는 setter 를 쓰지 않으므로, 렌더 중 ref 접근
  // 없이 자기 재귀가 가능하다 — deps `[]` 였던 기존과 동일하게 항상 같은 함수를 반환한다.
  const [scrollStep] = useState(() => {
    const step = () => {
      frameRef.current = null;
      const pointerY = pointerYRef.current;
      const scroller = scrollableAncestor(rootRef.current);
      if (pointerY === null || scroller === null) return;
      const rect = scroller.getBoundingClientRect();
      const delta = autoScrollDelta({
        pointerY,
        top: rect.top,
        bottom: rect.bottom,
        scrollTop: scroller.scrollTop,
        maxScrollTop: scroller.scrollHeight - scroller.clientHeight,
      });
      if (delta !== 0) scroller.scrollTop += delta;
      frameRef.current = requestAnimationFrame(step);
    };
    return step;
  });

  const trackPointer = useCallback(
    (event: DragEvent) => {
      pointerYRef.current = event.clientY;
      if (frameRef.current === null)
        frameRef.current = requestAnimationFrame(scrollStep);
    },
    [scrollStep]
  );

  useEffect(() => {
    if (!isDragging) return;
    document.addEventListener("dragover", trackPointer, true);
    return () => {
      document.removeEventListener("dragover", trackPointer, true);
      stopAutoScroll();
    };
  }, [isDragging, trackPointer, stopAutoScroll]);

  const handleDragStart = useCallback((index: number) => {
    dragItemIndexRef.current = index;
    setDragItemIndex(index);
    setIsDragging(true);
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    // 원래 위치로 돌아오면 dragOverIndex를 null로 설정하여 dropzone을 표시하지 않음
    if (dragItemIndexRef.current === index) {
      dragOverItemIndexRef.current = null;
      setDragOverIndex(null);
    } else {
      dragOverItemIndexRef.current = index;
      setDragOverIndex(index);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // 원래 위치로 돌아왔거나, 같은 위치에 드롭하면 아무 작업도 하지 않음
      if (
        dragItemIndexRef.current === null ||
        dragOverItemIndexRef.current === null ||
        dragItemIndexRef.current === dragOverItemIndexRef.current
      ) {
        dragItemIndexRef.current = null;
        dragOverItemIndexRef.current = null;
        setDragItemIndex(null);
        setDragOverIndex(null);
        setIsDragging(false);
        return;
      }

      // onDrop 콜백 호출
      onDrop(dragItemIndexRef.current, dragOverItemIndexRef.current);

      // 상태 리셋
      dragItemIndexRef.current = null;
      dragOverItemIndexRef.current = null;
      setDragItemIndex(null);
      setDragOverIndex(null);
      setIsDragging(false);
    },
    [onDrop]
  );

  const handleDragEnd = useCallback(() => {
    // 원래 위치에서 드래그를 끝내면 아무 작업도 하지 않음
    dragItemIndexRef.current = null;
    dragOverItemIndexRef.current = null;
    setDragItemIndex(null);
    setDragOverIndex(null);
    setIsDragging(false);
  }, []);

  const contextValue: DragAndDropContextValue = {
    dragItemIndex,
    dragOverIndex,
    isDragging,
    onDragStart: handleDragStart,
    onDragEnter: handleDragEnter,
    onDragEnd: handleDragEnd,
  };

  return (
    <DragAndDropContext.Provider value={contextValue}>
      <Box ref={rootRef} onDragOver={handleDragOver} onDrop={handleDrop} sx={sx}>
        {children}
      </Box>
    </DragAndDropContext.Provider>
  );
};

DragAndDrop.Item = DragAndDropItem;

/**
 * 드롭 위치 표시선 — 항목 바깥(항목 사이 간격 안)에 절대배치한다.
 *
 * 투명 border 로 자리를 미리 잡아두면 드래그하지 않는 평상시에도 항목마다 위아래 3px 이
 * 붙어, 목록이 선언한 `gap` 보다 6px 씩 벌어진 채로 보인다(같은 프리미티브를 쓰는
 * BIM·PointCloud 목록보다 GIS·선형 목록만 넓어 보였던 원인). 절대배치는 레이아웃을
 * 차지하지 않으므로 평상시 간격은 선언값 그대로이고, 드래그 중에도 밀림이 없다.
 */
const dropIndicator = {
  content: '""',
  position: "absolute",
  left: 0,
  right: 0,
  height: "3px",
  backgroundColor: "#1d74ff",
  pointerEvents: "none",
  transition: "opacity 0.2s ease-in-out",
} as const;
